import type {
    Condition,
    ConditionGroup,
    EditorMetadata,
    LogicalOperator,
} from "./types";

/**
 * The condition tree as the builder edits it, and the rules the engine imposes on it.
 *
 * Free of React so the three silent footguns below are unit-tested rather than eyeballed. All
 * three are real engine behaviour, all three are invisible in the admin today, and all three
 * produce a workflow that looks configured and does nothing (or everything).
 */

/** A draft group. Ids are absent for groups the user has just added — the server assigns them. */
export interface DraftExpression {
    key: string;
    fieldType: string;
    fieldName: string;
    operator: string;
    value: string;
    value2: string;
}

export interface DraftGroup {
    key: string;
    logicalOperator: LogicalOperator;
    dataProviders: string[];
    geoArea: string | null;
    expressions: DraftExpression[];
    children: DraftGroup[];
}

/**
 * Levels of groups a tree may have, root included.
 *
 * Mirrors MAX_CONDITION_NESTING_DEPTH in workflows/api/v1/serializers.py. The builder stops
 * offering sub-groups here so the limit is never reached by a rejected save.
 */
export const MAX_GROUP_LEVELS = 3;

let keyCounter = 0;

/** Stable client-side key for list rendering. Not sent to the server. */
export function nextKey(prefix: string): string {
    keyCounter += 1;
    return `${prefix}-${keyCounter}`;
}

export function emptyExpression(fieldType = "numeric"): DraftExpression {
    return {
        key: nextKey("expr"),
        fieldType: fieldType,
        fieldName: "",
        operator: "gt",
        value: "",
        value2: "",
    };
}

export function emptyGroup(): DraftGroup {
    return {
        key: nextKey("group"),
        logicalOperator: "AND",
        dataProviders: [],
        geoArea: null,
        // A new group starts with an expression, because an empty one matches every event and
        // handing the user that state by default is how the footgun gets pulled.
        expressions: [emptyExpression()],
        children: [],
    };
}

/** Convert a condition from the API into an editable draft. */
export function toDraft(condition: Condition): DraftGroup[] {
    const convert = (group: ConditionGroup): DraftGroup => ({
        key: `group-${group.id}`,
        logicalOperator: group.logicalOperator,
        dataProviders: group.dataProviders.map(String),
        geoArea: group.geoArea,
        expressions: group.expressions.map((expression) => ({
            key: `expr-${expression.id}`,
            fieldType: expression.fieldType,
            fieldName: expression.fieldName,
            operator: expression.operator,
            value: expression.value,
            value2: expression.value2 ?? "",
        })),
        children: group.children.map(convert),
    });

    return condition.groups.map(convert);
}

/** Convert a draft back into the tree-endpoint payload. Positions come from list order. */
export function toPayload(groups: DraftGroup[]): unknown[] {
    const convert = (group: DraftGroup): unknown => ({
        logical_operator: group.logicalOperator,
        data_providers: group.dataProviders,
        geo_area: group.geoArea,
        expressions: group.expressions.map((expression) => ({
            field_type: expression.fieldType,
            field_name: expression.fieldName,
            operator: expression.operator,
            value: expression.value,
            value2: expression.value2,
        })),
        children: group.children.map(convert),
    });

    return groups.map(convert);
}

/** The operators legal for a field type, from the metadata endpoint. */
export function operatorsFor(
    metadata: EditorMetadata,
    fieldType: string,
): string[] {
    return (
        metadata.fieldTypes.find((entry) => entry.value === fieldType)
            ?.operators ?? []
    );
}

/**
 * Snap an operator to a legal one for the field type.
 *
 * extra_data and site_extra_data are compared as strings and the evaluator raises on anything
 * but eq/neq — at evaluation time, against a live event. Changing field type therefore has to
 * change the operator with it rather than leaving an unsaveable state the server will reject.
 */
export function snapOperator(
    metadata: EditorMetadata,
    fieldType: string,
    operator: string,
): string {
    const legal = operatorsFor(metadata, fieldType);
    if (!legal.length) {
        return operator;
    }
    return legal.includes(operator) ? operator : legal[0];
}

/** Whether a group has nothing that could narrow anything. */
export function isVacuousGroup(group: DraftGroup): boolean {
    return (
        group.expressions.length === 0 &&
        group.children.length === 0 &&
        group.dataProviders.length === 0 &&
        group.geoArea === null
    );
}

export interface TreeWarning {
    /** The group this is about, or null when it concerns the condition as a whole. */
    groupKey: string | null;
    message: string;
}

/**
 * The engine behaviours a user has to be told about before saving.
 *
 * 1. An empty group evaluates **true** — it matches every event.
 * 2. A condition with no root groups evaluates **false** — it never fires.
 * 3. Root groups are OR'd, while the operator inside a group applies within it.
 *
 * The third is not a warning, but it is stated in the UI: nothing about a stack of root groups
 * says which operator joins them, and reading it as AND inverts the condition.
 */
export function treeWarnings(groups: DraftGroup[]): TreeWarning[] {
    const warnings: TreeWarning[] = [];

    if (groups.length === 0) {
        warnings.push({
            groupKey: null,
            message:
                "A condition with no groups never matches, so nothing using it will ever fire. If you meant “always match”, remove the condition instead.",
        });
        return warnings;
    }

    const walk = (group: DraftGroup) => {
        if (isVacuousGroup(group)) {
            warnings.push({
                groupKey: group.key,
                message:
                    "This group has no expressions, sub-groups, data providers or geo area, so it matches every event.",
            });
        }
        for (const expression of group.expressions) {
            if (!expression.fieldName) {
                warnings.push({
                    groupKey: group.key,
                    message: "An expression has no field selected.",
                });
            }
            if (expression.value === "") {
                warnings.push({
                    groupKey: group.key,
                    message: `An expression on ${
                        expression.fieldName || "an unselected field"
                    } has no value.`,
                });
            }
            if (expression.operator === "between" && expression.value2 === "") {
                warnings.push({
                    groupKey: group.key,
                    message: `A "between" expression on ${
                        expression.fieldName || "an unselected field"
                    } needs an upper bound.`,
                });
            }
        }
        group.children.forEach(walk);
    };

    groups.forEach(walk);
    return warnings;
}

/**
 * Whether the tree would be rejected outright rather than merely warned about.
 *
 * Incomplete expressions are hard errors at the API — a missing value or a missing upper bound
 * fails serializer validation — while a vacuous group is a warning the user may acknowledge.
 * Separating them keeps the Save button honest about which is which.
 */
export function hasBlockingProblems(groups: DraftGroup[]): boolean {
    const blocking = (group: DraftGroup): boolean => {
        for (const expression of group.expressions) {
            if (!expression.fieldName || expression.value === "") {
                return true;
            }
            if (expression.operator === "between" && expression.value2 === "") {
                return true;
            }
        }
        return group.children.some(blocking);
    };

    return groups.some(blocking);
}

/** Replace a group anywhere in the tree, returning a new tree. */
export function updateGroup(
    groups: DraftGroup[],
    key: string,
    update: (group: DraftGroup) => DraftGroup,
): DraftGroup[] {
    return groups.map((group) => {
        if (group.key === key) {
            return update(group);
        }
        if (group.children.length) {
            return {
                ...group,
                children: updateGroup(group.children, key, update),
            };
        }
        return group;
    });
}

/** Remove a group anywhere in the tree, returning a new tree. */
export function removeGroup(groups: DraftGroup[], key: string): DraftGroup[] {
    return groups
        .filter((group) => group.key !== key)
        .map((group) =>
            group.children.length
                ? { ...group, children: removeGroup(group.children, key) }
                : group,
        );
}
